/* eslint no-alert: 0 */
import cornerstoneTools from 'cornerstone-tools';
import Formatter from '../formatter';

const BaseAnnotationTool = cornerstoneTools.importInternal('base/BaseAnnotationTool');

const EVENTS = cornerstoneTools.EVENTS;

const moveNewHandle = cornerstoneTools.import(
    'manipulators/moveNewHandle',
)

const drawHandles = cornerstoneTools.import('drawing/drawHandles')
const drawRect = cornerstoneTools.import('drawing/drawRect');

const pointInsideBoundingBox = cornerstoneTools.importInternal('util/pointInsideBoundingBox');
const triggerEvent = cornerstoneTools.importInternal('util/triggerEvent');
const drawLinkedTextBox = cornerstoneTools.importInternal('drawing/drawLinkedTextBox');
const getNewContext = cornerstoneTools.import('drawing/getNewContext');
const draw = cornerstoneTools.import('drawing/draw');
const setShadow = cornerstoneTools.import('drawing/setShadow');

const arrowAnnotateCursor = cornerstoneTools.importInternal('cursors/index');

export function textBoxWidth(context, text, padding) {
    const font = cornerstoneTools.textStyle.getFont();
    const origFont = 12;

    const width = context.measureText(text).width;

    if (font && font !== origFont) {
        context.font = origFont;
    }

    return width + 2 * padding;
}


/**
 * @public
 * @class ArrowAnnotateTool
 * @memberof Tools.Annotation
 * @classdesc Create and position an arrow and label
 * @extends Tools.Base.BaseAnnotationTool
 */
export default class FormAnnotateToolEyonix extends BaseAnnotationTool {
    constructor(props = {}) {
        const defaultProps = {
            name: 'FormAnnotate',
            supportedInteractionTypes: ['Mouse', 'Touch'],
            configuration: {
                getTextCallback: getTextCallback,
                changeTextCallback: changeTextCallback,
                drawHandles: true,
                drawHandlesOnHover: true,
                hideHandlesIfMoving: false,
                arrowFirst: true,
                renderDashed: false,
                allowEmptyLabel: false,
            },
            svgCursor: arrowAnnotateCursor,
        };

        super(props, defaultProps);
        this.preventNewMeasurement = false;
    }

    createNewMeasurement(evt) {
        // Create the measurement data for this tool with the end handle activated
        return {
            visible: true,
            active: true,
            color: undefined,
            fontSize: undefined,
            textVisible: undefined,
            who: undefined,
            when: undefined,
            whenStr: undefined,
            invalidated: true,
            data: {},
            initialRotation: evt.detail.viewport.rotation,
            handles: {
                start: {
                    x: evt.detail.currentPoints.image.x,
                    y: evt.detail.currentPoints.image.y,
                    highlight: true,
                    active: false,
                },
                end: {
                    x: evt.detail.currentPoints.image.x + 150,
                    y: evt.detail.currentPoints.image.y + 150,
                    highlight: true,
                    active: false,
                },
                textBox: {
                    active: true,
                    hasMoved: false,
                    movesIndependently: false,
                    drawnIndependently: true,
                    allowedOutsideImage: true,
                    hasBoundingBox: true,
                },
            },
        };
    }

    pointNearTool(element, data, coords) {
        if (data.visible === false) {
            return false;
        }

        const startX = data.handles.end.x < data.handles.start.x? data.handles.end.x: data.handles.start.x;
        const startY = data.handles.end.y < data.handles.start.y? data.handles.end.y: data.handles.start.y;
        let endX = data.handles.end.x < data.handles.start.x? data.handles.start.x: data.handles.end.x;
        let endY =  data.handles.end.y < data.handles.start.y? data.handles.start.y: data.handles.end.y;

        if(data.handles.end.x === data.handles.start.x || data.handles.end.y === data.handles.start.y){
            endX = data.handles.start.x + 150;
            endY = data.handles.start.y + 150;
        }

        return window.cornerstone.pixelToCanvas(element, {x: startX, y: startY}).x  < coords.x &&
                window.cornerstone.pixelToCanvas(element, {x: endX , y: startY}).x  > coords.x &&
                window.cornerstone.pixelToCanvas(element, {x: startX, y: startY}).y  < coords.y &&
                window.cornerstone.pixelToCanvas(element, {x: startX, y: endY}).y  > coords.y;
    }

    updateCachedStats() {
        // Implementing to satisfy BaseAnnotationTool
    }

    renderToolData(evt) {
        const { element, enabledElement } = evt.detail;
        const {
            handleRadius,
            drawHandlesOnHover,
            hideHandlesIfMoving,
            renderDashed,
        } = this.configuration;

        // If we have no toolData for this element, return immediately as there is nothing to do
        const toolData = cornerstoneTools.getToolState(element, this.name);

        if (!toolData) {
            return;
        }

        // We have tool data for this element - iterate over each one and draw it
        const canvas = evt.detail.canvasContext.canvas;
        const context = getNewContext(canvas);

        const lineWidth = cornerstoneTools.toolStyle.getToolWidth();
     //   const hideHandlers = evt.detail.viewport.hideHanders || false;

        for (let i = 0; i < toolData.data.length; i++) {
            const data = toolData.data[i];

            if (data.visible === false) {
                continue;
            }

            let viewport = window.cornerstone.getViewport(element);

            const scale = viewport.scale;


            draw(context, context => {
                setShadow(context, this.configuration);


                const color = "#111111"; //cornerstoneTools.toolColors.getColorIfActive(data);
                data.color = "#111111";  //data.color || cornerstoneTools.toolColors.getToolColor(data);

                let initFontSize = cornerstoneTools.textStyle.getFontSize();

                data.fontSize = data.fontSize !== undefined? data.fontSize : cornerstoneTools.textStyle.getFontSize();
                data.textVisible = data.textVisible !== undefined? data.textVisible : this.options.annotationTextVisible;

                cornerstoneTools.textStyle.setFont(`${data.fontSize}px Roboto`);
                cornerstoneTools.textStyle.setFontSize(data.fontSize);


                if(data.invalidated && (data.who !== this.initialConfiguration.user || data.when !== new Date().toLocaleString())) {
                    data.who = this.initialConfiguration.user;
                    data.when = new Date().toLocaleString();
                    if(this.initialConfiguration.momentLib && this.initialConfiguration.defaultDateTimeFormat) {
                        data.whenStr = this.initialConfiguration.momentLib().format(this.initialConfiguration.defaultDateTimeFormat);
                    } else {
                        data.whenStr = new Date().toLocaleString();
                    }
                }

                const textAllergy = data.data.text;
                const padding = 24;

                const startX = data.handles.end.x < data.handles.start.x? data.handles.end.x: data.handles.start.x;
                const startY = data.handles.end.y < data.handles.start.y? data.handles.end.y: data.handles.start.y;

                let endX = data.handles.end.x < data.handles.start.x? data.handles.start.x: data.handles.end.x;
                let endY =  data.handles.end.y < data.handles.start.y? data.handles.start.y: data.handles.end.y;


                if(data.handles.end.x === data.handles.start.x || data.handles.end.y === data.handles.start.y){
                    endX = data.handles.start.x + 150;
                    endY = data.handles.start.y + 150;
                }


                const startPoint = window.cornerstone.pixelToCanvas(element, {x: startX , y: startY})
                const endPoint = window.cornerstone.pixelToCanvas(element, {x: endX , y: endY})

                const drugAllergy = data.data.allergy;

                let textRows = [];
                if(textAllergy) textRows = Formatter.fragmentCanvasText(context, textAllergy, 130);

                function getCanvasPoint(dx, dy) {
                    return window.cornerstone.pixelToCanvas(element, {x: startX + dx , y: startY + dy})
                }

                let point  = getCanvasPoint(10, 30);

                const fontSizeX = (9 * scale * (endX - startX))/150;
                const fontSizeY = (9 * scale * (endY - startY))/150;
                const fontSize = fontSizeX > fontSizeY? fontSizeY: fontSizeX;

                const formScaleX = (endX - startX)/150;
                const formScaleY = (endY - startY)/150;
                let formScale = formScaleX > formScaleY? formScaleY: formScaleX;

                formScale = formScale * viewport.scale;

                const rectOptions = { color };

                context.fillStyle = "#ffffff";
                context.fillRect(startPoint.x, startPoint.y, endPoint.x - startPoint.x, endPoint.y - startPoint.y);

                drawRect(
                    context,
                    element,
                    data.handles.start,
                    data.handles.end,
                    rectOptions,
                    'pixel',
                    data.handles.initialRotation
                );

                context.font = ` ${fontSize}px Verdana`;
                context.fillStyle = "#111111";

                const handleOptions = {
                    color: "#111111",
                    handleRadius,
                    drawHandlesIfActive: drawHandlesOnHover,
                    hideHandlesIfMoving,
                };

                if (data.active) {
                    drawHandles(context, evt.detail, data.handles, handleOptions);
                }

                const rotation = viewport.rotation;
                const initRot = data.initialRotation || 0;

                let deg =  (rotation - initRot) * (Math.PI/180);
                if(rotation === initRot) {
                    deg = 0;
                }
                context.save();
                context.translate(startPoint.x, startPoint.y);
                context.rotate(deg);

                //const textStartPoint = window.cornerstone.pixelToCanvas(element, {x: startX + 10, y: startY+(10*formScale)})

                context.fillText(`Drug Allergy: No [${drugAllergy === "no" ? "\u{2714}": ""}] Yes [${drugAllergy === "yes"? "\u{2714}": ""}] `, 10*formScale,  10* formScale);

               //point  = getCanvasPoint(10, 20*formScale);
                context.fillText(`If yes, please specify.,`, 10*formScale,  20*formScale);

                context.fillStyle = "#ff0e0e";

                textRows.forEach((textRow, index)=> {
                  //  point  = getCanvasPoint(10, (30+index*10)*formScale);
                    context.fillText(textRow, 10*formScale, (30+index*10)*formScale );
                });

                context.fillStyle = "#111111";

                function renderCheckboxRow(text, value, startX, startY, textRow2) {
                   // let point = getCanvasPoint(startX, startY*formScale);

                    if(value) {
                        context.font = `bold ${fontSize}px Verdana`;
                        context.fillText('\u{2611}', startX*formScale, startY*formScale );
                    } else {
                        context.font = `${fontSize}px Verdana`;
                        context.fillText('\u{2610}', startX*formScale, startY*formScale );
                    }

                  //  point = getCanvasPoint(startX+12*formScale, startY*formScale);
                    context.fillText(text, (startX+12)*formScale, startY*formScale );

                    if(textRow2){
                      //  point = getCanvasPoint(startX+12*formScale, (startY+10)*formScale);
                        context.fillText(textRow2, (startX+12)*formScale, (startY+10)*formScale );
                    }
                }

                context.beginPath();
                context.moveTo(0, ((60*formScale)));
                context.lineTo(Math.abs(endPoint.x - startPoint.x), ((60*formScale)));
                context.stroke();

                renderCheckboxRow("G. Proparacaine 0.5%", data.data.proparacaine, 10, 50+padding);
                renderCheckboxRow("G. Tetracaine 0.5%", data.data.tetracaine05, 10, 60+padding, "(minims)");
                renderCheckboxRow("G. Tetracaine 1% ", data.data.tetracaine1, 10, 80+padding, "(minims)");
                renderCheckboxRow("Fluorescein Strip", data.data.fluorescein, 10, 100+padding);

                context.restore();

                const tooltipText =  `${data.who} (${data.whenStr})` ;

                if((data.active || this.options.showUser) && data.who) {
                    drawLinkedTextBox(
                        context,
                        element,
                        {
                            x: endX - (endX - startX)/2,
                            y: endY - (endY - startY)/2,
                        },
                        tooltipText,
                        data.handles,
                        textBoxAnchorPoints,
                        "white",
                        lineWidth,
                        0,
                        false
                    );
                }

                data.invalidated = false;

                cornerstoneTools.textStyle.setFontSize(initFontSize);
                cornerstoneTools.textStyle.setFont(`${initFontSize}px Roboto`);
            });
        }

        function textBoxAnchorPoints(handles) {
            const midpoint = {
                x: (handles.start.x + handles.end.x) / 2,
                y: (handles.start.y + handles.end.y) / 2,
            };

            return [handles.start, midpoint, handles.end];
        }
    }

    addNewMeasurement(evt, interactionType) {
        const element = evt.detail.element;
        const measurementData = this.createNewMeasurement(evt);

        const { allowEmptyLabel } = this.configuration;

        // Associate this data with this imageId so we can render it and manipulate it
        cornerstoneTools.addToolState(element, this.name, measurementData);
        window.cornerstone.updateImage(element);

        if (measurementData.text === undefined) {
            this.configuration.getTextCallback(newData => {
                if (newData || allowEmptyLabel) {
                    measurementData.data.text = newData.text;
                    measurementData.data.allergy = newData.allergy;
                    measurementData.data.proparacaine = newData.proparacaine;
                    measurementData.data.tetracaine05 = newData.tetracaine05;
                    measurementData.data.tetracaine1 = newData.tetracaine1;
                    measurementData.data.fluorescein = newData.fluorescein;

                    measurementData.active = false;

                    const modifiedEventData = {
                        toolName: this.name,
                        toolType: this.name, // Deprecation notice: toolType will be replaced by toolName
                        element,
                        measurementData,
                    };

                    window.cornerstone.updateImage(element);
                    triggerEvent(
                        element,
                        EVENTS.MEASUREMENT_COMPLETED,
                        modifiedEventData
                    );
                } else {
                    cornerstoneTools.removeToolState(element, this.name, measurementData);
                }
            }, evt.detail);
        }

    }

    doubleClickCallback(evt) {
        return this._updateTextForNearbyAnnotation(evt);
    }

    touchPressCallback(evt) {
        return this._updateTextForNearbyAnnotation(evt);
    }

    _updateTextForNearbyAnnotation(evt) {
        const element = evt.detail.element;
        const coords = evt.detail.currentPoints.canvas;
        const toolState = cornerstoneTools.getToolState(element, this.name);

        if (!toolState) {
            return false;
        }

        for (let i = 0; i < toolState.data.length; i++) {
            const data = toolState.data[i];

            if (
                this.pointNearTool(element, data, coords) ||
                pointInsideBoundingBox(data.handles.textBox, coords)
            ) {
                data.active = true;
                window.cornerstone.updateImage(element);

                // Allow relabelling via a callback
                this.configuration.changeTextCallback(
                    data,
                    evt.detail,
                    this._doneChangingTextCallback.bind(this, element, data)
                );

                evt.stopImmediatePropagation();
                evt.preventDefault();
                evt.stopPropagation();

                return true;
            }
        }
    }

    _doneChangingTextCallback(element, measurementData, newData, deleteTool) {
        if (deleteTool === true) {
            cornerstoneTools.removeToolState(element, this.name, measurementData);
        } else {
            measurementData.data.text = newData.text;
            measurementData.data.allergy = newData.allergy;
            measurementData.data.proparacaine = newData.proparacaine;
            measurementData.data.tetracaine05 = newData.tetracaine05;
            measurementData.data.tetracaine1 = newData.tetracaine1;
            measurementData.data.fluorescein = newData.fluorescein;
        }

        measurementData.active = false;
        window.cornerstone.updateImage(element);

        triggerEvent(element, EVENTS.MEASUREMENT_MODIFIED, {
            toolName: this.name,
            toolType: this.name, // Deprecation notice: toolType will be replaced by toolName
            element,
            measurementData,
        });
    }
}


function getTextCallback(doneChangingTextCallback) {
    const customDoneChangingTextCallback = () => {
        let all = document.querySelector('input[name="allergy"]:checked')?.value;

        doneChangingTextCallback({
            text: document.getElementById("annotation-text-2").value,
            allergy: all,
            proparacaine: document.getElementById("proparacaine").checked,
            tetracaine05: document.getElementById("tetracaine05").checked,
            tetracaine1: document.getElementById("tetracaine1").checked,
            fluorescein: document.getElementById("fluorescein").checked
        });

        document.getElementById("form-tool-modal").style.display = "none";
        document.getElementById("save-annotation-2").removeEventListener("click", customDoneChangingTextCallback);
        document.getElementById("save-annotation-2").removeEventListener("click", closeModal);
    };

    const closeModal = () => {
        document.getElementById("form-tool-modal").style.display = "none";
        document.getElementById("save-annotation-2").removeEventListener("click", customDoneChangingTextCallback);
        document.getElementById("save-annotation-2").removeEventListener("click", closeModal);
    };

    document.getElementById("form-allergy-no").checked = false;
    document.getElementById("form-allergy-yes").checked = false;
    document.getElementById("annotation-text-2").value = "";
    document.getElementById("proparacaine").checked = false;
    document.getElementById("tetracaine05").checked = false;
    document.getElementById("tetracaine1").checked = false;
    document.getElementById("fluorescein").checked = false;
    document.getElementById("form-tool-modal").style.display = "block";
    document.getElementById("save-annotation-2").addEventListener("click", customDoneChangingTextCallback);
    document.getElementById("cancel-annotation-2").addEventListener("click", closeModal);

}

function changeTextCallback(data, eventData, doneChangingTextCallback) {
    const customDoneChangingTextCallback = () => {
        let all = document.querySelector('input[name="allergy"]:checked')?.value;

        doneChangingTextCallback({
            text: document.getElementById("annotation-text-2").value,
            allergy: all,
            proparacaine: document.getElementById("proparacaine").checked,
            tetracaine05: document.getElementById("tetracaine05").checked,
            tetracaine1: document.getElementById("tetracaine1").checked,
            fluorescein: document.getElementById("fluorescein").checked
        });

        document.getElementById("form-tool-modal").style.display = "none";
        document.getElementById("save-annotation-2").removeEventListener("click", customDoneChangingTextCallback);
        document.getElementById("save-annotation-2").removeEventListener("click", closeModal);
    };

    const closeModal = () => {
        document.getElementById("form-tool-modal").style.display = "none";
        document.getElementById("save-annotation-2").removeEventListener("click", customDoneChangingTextCallback);
        document.getElementById("save-annotation-2").removeEventListener("click", closeModal);
    };

    document.getElementById("form-tool-modal").style.display = "block";
    document.getElementById("annotation-text-2").value = data.data.text;

    if(data.data.allergy === "yes") document.getElementById("form-allergy-yes").checked = true;
    if(data.data.allergy === "no") document.getElementById("form-allergy-no").checked = true;

    document.getElementById("proparacaine").checked = (data.data.proparacaine? data.data.proparacaine: false);
    document.getElementById("tetracaine05").checked =  (data.data.tetracaine05? data.data.tetracaine05: false);
    document.getElementById("tetracaine1").checked = data.data.tetracaine1? data.data.tetracaine1: false;
    document.getElementById("fluorescein").checked = data.data.fluorescein? data.data.fluorescein: false;
    document.getElementById("save-annotation-2").addEventListener("click", customDoneChangingTextCallback);
    document.getElementById("cancel-annotation-2").addEventListener("click", closeModal);

}